// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * **Search people** - the network half (`docs/ux/who-can-find-me.md`
 * section 6.1, ADR-0060 "Client contract (S4 / S5)", Search).
 *
 * One search asks, in parallel:
 * - the **channel** namespace for the typed handle (kept from the old
 *   "Look up @name" row: the person does not know which namespace a name
 *   they were given lives in);
 * - the **username** (`/discovery/lookup`, which answers for people in
 *   Everyone only);
 * - **one name term** (`nameQueryTerm` -> `/discovery/name/lookup`), people
 *   in Everyone whose published name carries it;
 * then, with the OPRF outputs of the username and the term, the kept
 * **introductions** (contacts of contacts).
 *
 * Every OPRF round yields two values (`discovery/oprf-pair.ts`): the
 * `lookupKey` is what discovery is asked with and what a username certificate
 * binds; the `output` opens records and never leaves the device.
 *
 * Everything is filtered by the WHOLE query (the network matched one term),
 * a username is shown only when verified, and a result found through a
 * grant is shown only after the DS reach check says the grant still opens
 * the person's mailbox. A check that says no drops the result silently: the
 * searcher must not be able to tell "hidden" from "does not exist".
 *
 * **What a person in Everyone is called.** Both lookups return, beside each
 * account id, the person's PUBLIC RECORD (ADR-0060 section 5), sealed under
 * the OPRF outputs of their name terms and username. It is opened with the
 * OUTPUT of the round that found the hit (never its lookup key) and counts
 * only when it names both the hit's account id and the key the identity
 * bundle serves for that account ({@link acceptPublicRecord}); a record that
 * does not open or fails either check is ignored. A name this device already
 * holds (a contact, a profile from a shared group or channel) wins over the
 * record's. A name hit nothing names is dropped; a username match without a
 * usable record is shown under its username. The record also states the
 * person's username and its certificate: a name hit shows the username once
 * the certificate proves it, as an introduction's does, and never before.
 *
 * Every dependency is an argument, so the whole decision is testable.
 */
import { base64Decode } from "../auth/account-id";
import type { OprfPair } from "../discovery/oprf-pair";
import { fingerprintLabel } from "../profile/resolver";
import type { IntroductionProbe, OpenedIntroduction } from "./introductions";
import {
  keyB64Of,
  mergePeople,
  shownUsername,
  toHex,
  type PersonReach,
  type PersonResult,
} from "./people";
import { foldSearch, legOutcomeOf, type LegOutcome, type PeopleSearchState } from "./search-state";

/** What this device knows about a person, by account key (base64). */
export interface KnownPerson {
  /** A name this device holds (rename or profile); `null` for none. */
  readonly name: string | null;
  /** For a contact nothing names: what they are shown by anyway (their
   *  verified username, else their fingerprint). A name they published wins
   *  over it; with none, the contact still shows by it. `null` for anyone
   *  who is not a contact, and for a contact with a held `name`. */
  readonly contactFallbackName: string | null;
  readonly verifiedUsername: string | null;
  readonly isContact: boolean;
  /** Every grant kept for them, in the order they are tried. */
  readonly keptGrantWires: readonly string[];
}

/** One lookup hit: an account in Everyone and its public record (standard
 *  bytes; `null` when discovery sent none). */
export interface LookupHit {
  readonly accountId: Uint8Array;
  readonly publicRecord: Uint8Array | null;
}

/** The fields of an opened public record the search reads. */
export interface OpenedPublicRecord {
  readonly identityPk: Uint8Array;
  readonly accountId: Uint8Array;
  readonly displayName: string | null;
  /** The username the record states, and the discovery certificate for it. A
   *  claim until {@link PeopleSearchDeps.verifyUsername} says otherwise. */
  readonly username?: string | null;
  readonly usernameCertificate?: Uint8Array | null;
  /** ADR-0078 - the person's 97-byte mailbox binding, the core verified
   *  against `identityPk`. Absent from a fake that does not model it. */
  readonly mailboxBinding?: Uint8Array | null;
}

export interface PeopleSearchDeps {
  /** The canonical username the text could be, or `null` (`looksLikeAName`
   *  + `bareName`). No username or channel question is asked without one. */
  canonicalUsername(typed: string): string | null;
  /** `nameQueryTerm`: the one term looked up, `null` when not searchable. */
  queryTerm(query: string): string | null;
  /** `nameMatchesQuery`: the post-filter over the whole query. */
  nameMatches(displayName: string, query: string): boolean;
  /** The channel namespace; `null` when not a channel. Omitted: not asked. */
  lookupChannel?(handle: string): Promise<string | null>;
  /** One OPRF round over a username (one anonymous token). */
  usernameOprf(username: string): Promise<OprfPair>;
  /** The lookup key of a username a record states, kept once it is known so a
   *  person found again costs no token. Absent: one {@link usernameOprf}. */
  usernameLookupKey?(username: string): Promise<Uint8Array>;
  /** `/discovery/lookup` by the username's LOOKUP KEY: the account id and
   *  its public record, or `null`. */
  accountOfUsernameLookupKey(lookupKey: Uint8Array): Promise<LookupHit | null>;
  /** One OPRF round over a name term (one anonymous token). */
  nameTermOprf(term: string): Promise<OprfPair>;
  /** `/discovery/name/lookup` by the term's LOOKUP KEY: accounts in
   *  Everyone carrying the term, with their public records. */
  accountsOfNameTermLookupKey(lookupKey: Uint8Array): Promise<LookupHit[]>;
  /** `openPublicRecord(record, oprfOutput)`: the opened fields, or `null`
   *  when it does not open. Never throws. */
  openPublicRecord(record: Uint8Array, oprfOutput: Uint8Array): OpenedPublicRecord | null;
  /** The account key the identity bundle publishes, `null` for none. */
  identityKeyOf(accountId: Uint8Array): Promise<Uint8Array | null>;
  /** ADR-0074 section 3.5a - whether the key the account's identity bundle
   *  publishes is the PROVEN successor of `foundKey` (the old key signed the
   *  new one): the same person, found under the key of before a rotation.
   *  Absent: only an exact match is accepted. */
  provenPredecessor?(foundKey: Uint8Array, accountId: Uint8Array): Promise<boolean>;
  /** What this device knows about a person. */
  known(keyB64: string): KnownPerson;
  /** Open the kept introduction records with these probes. */
  openIntroductions(probes: readonly IntroductionProbe[]): Promise<OpenedIntroduction[]>;
  /** `verifyUsernameBinding` against the pinned / fetched key: `true` only
   *  when the certificate binds the username's `lookupKey` (never its OPRF
   *  output) to `accountId`. Never throws. */
  verifyUsername(certificate: Uint8Array, usernameLookupKey: Uint8Array, accountId: Uint8Array): Promise<boolean>;
  /** The DS reach check of the person's account mailbox with a grant. */
  reachCheck(identityPk: Uint8Array, grantWireB64: string): Promise<boolean>;
  /** Standard base64 of a grant wire. */
  encodeGrant(wire: Uint8Array): string;
  readonly ownKeyHex: string | null;
  /** How many introduction records {@link openIntroductions} went through;
   *  0 when it was not called. Absent: not counted. */
  introductionRecordsHeld?(): number;
  /** Called once when a search finishes. */
  logCounts?(counts: SearchCounts): void;
  /** ADR-0087 section 4 — the sealed record of a public group or channel the
   *  exact `@name` names, or `null` (`/discovery/lookup` answering `handle`).
   *  Absent: groups are not asked. */
  conversationRecordOfLookupKey?(lookupKey: Uint8Array): Promise<Uint8Array | null>;
  /** The sealed records of public groups and channels carrying a name term. */
  conversationRecordsOfTermLookupKey?(lookupKey: Uint8Array): Promise<Uint8Array[]>;
  /** `openSearchRecord` for a conversation, with the OUTPUT of the round that
   *  found it; `null` when it does not open. Never throws. */
  openConversationRecord?(record: Uint8Array, oprfOutput: Uint8Array): FoundConversation | null;
}

/** ADR-0087 section 5 — a public group or channel found by search. */
export interface FoundConversation {
  readonly kind: "group" | "channel";
  /** The group id (32 bytes) or channel id (16), base64. */
  readonly idB64: string;
  readonly handle: string;
  readonly name: string;
  readonly description: string;
  /** The ADR-0059 band, 0..5. */
  readonly sizeBand: number;
  /** A group's knock public key, a channel's read key. */
  readonly key: Uint8Array;
  /** The version of a group's knock key: the request names it (ADR-0087
   *  section 5). 0 for a channel. */
  readonly knockVersion: number;
  readonly avatar: import("../profile/avatar-model").AvatarData | null;
}

/** What one finished search went through: counts only, never a name, key,
 *  query or grant, so they are safe to log. */
export interface SearchCounts {
  readonly recordsHeld: number;
  readonly recordsOpened: number;
  /** Opened records the whole query matched. */
  readonly keptAfterMatch: number;
  /** Results dropped because the reach check said no. */
  readonly refusedByReachCheck: number;
  /** People in the answer. */
  readonly people: number;
}

export interface PeopleSearchOutcome {
  readonly people: readonly PersonResult[];
  readonly channel: { readonly idHex: string; readonly handle: string } | null;
  readonly state: PeopleSearchState;
  /** ADR-0087 section 5 — public groups and channels (absent: not asked). */
  readonly conversations?: readonly FoundConversation[];
}

/** At most this many usernames that a public record or an introduction states
 *  are verified per search beyond the typed one: each verification costs an
 *  OPRF evaluation, i.e. one anonymous token. The rest show their name without
 *  a username. */
export const MAX_EXTRA_USERNAME_CHECKS = 3;

/**
 * The display name a public record gives a lookup hit, or `null`. The record
 * is opened with the OPRF OUTPUT of the round that found the hit, and counts
 * only when it names the hit's account id AND the key the identity bundle
 * serves for that account: any person in Everyone can seal a record naming
 * anyone, and discovery serves whatever the account published. Never throws.
 */
export function acceptPublicRecord(
  deps: Pick<PeopleSearchDeps, "openPublicRecord">,
  hit: LookupHit,
  oprfOutput: Uint8Array,
  bundleKey: Uint8Array,
): string | null {
  const name = acceptedRecord(deps, hit, oprfOutput, bundleKey)?.displayName?.trim() ?? "";
  return name.length === 0 ? null : name;
}

/** The record of a lookup hit, under the checks of {@link acceptPublicRecord},
 *  or `null`. Never throws. */
function acceptedRecord(
  deps: Pick<PeopleSearchDeps, "openPublicRecord">,
  hit: LookupHit,
  oprfOutput: Uint8Array,
  bundleKey: Uint8Array,
): OpenedPublicRecord | null {
  if (hit.publicRecord === null) return null;
  let opened: OpenedPublicRecord | null;
  try {
    opened = deps.openPublicRecord(hit.publicRecord, oprfOutput);
  } catch {
    return null;
  }
  if (opened === null) return null;
  if (toHex(opened.accountId) !== toHex(hit.accountId)) return null;
  if (toHex(opened.identityPk) !== toHex(bundleKey)) return null;
  return opened;
}

/** A username a record states, to be proven before it is shown. */
interface UsernameClaim {
  readonly username: string;
  readonly certificate: Uint8Array;
  readonly accountId: Uint8Array;
}

/** The username claim of a lookup hit's record, or `null`. Never throws. */
function publicRecordClaim(
  deps: Pick<PeopleSearchDeps, "openPublicRecord">,
  hit: LookupHit,
  oprfOutput: Uint8Array,
  bundleKey: Uint8Array,
): UsernameClaim | null {
  const opened = acceptedRecord(deps, hit, oprfOutput, bundleKey);
  const username = opened?.username ?? null;
  const certificate = opened?.usernameCertificate ?? null;
  if (username === null || certificate === null) return null;
  return { username, certificate, accountId: hit.accountId };
}

/**
 * ADR-0078 - the mailbox binding a public record gives a lookup hit (standard
 * base64), or `null`: under exactly the checks of {@link acceptPublicRecord}
 * (the record names the hit's account id AND the key the identity bundle
 * serves). The sender verifies it again against the key before writing.
 */
export function publicRecordBinding(
  deps: Pick<PeopleSearchDeps, "openPublicRecord" | "encodeGrant">,
  hit: LookupHit,
  oprfOutput: Uint8Array,
  bundleKey: Uint8Array,
): string | null {
  const binding = acceptedRecord(deps, hit, oprfOutput, bundleKey)?.mailboxBinding ?? null;
  return binding === null ? null : deps.encodeGrant(binding);
}

/** A result built from what is known locally, over a network finding. */
function resultFor(
  deps: PeopleSearchDeps,
  keyB64: string,
  keyHex: string,
  accountIdHex: string | null,
  foundReach: "open" | "grant",
  foundName: string | null,
  foundUsername: string | null,
  grantWireB64: string | null,
  openBindingB64: string | null = null,
): PersonResult | null {
  const k = deps.known(keyB64);
  const reach: PersonReach = k.isContact ? "contact" : foundReach;
  // A held name, else the name found (published), else a contact's
  // fingerprint / username stand-in.
  const name = k.name ?? foundName ?? k.contactFallbackName;
  if (name === null) return null;
  const nameIsFallback = k.name === null && foundName === null;
  return {
    keyB64,
    keyHex,
    accountIdHex,
    name,
    ...(nameIsFallback ? { nameIsFallback: true } : {}),
    username: shownUsername(foundUsername ?? k.verifiedUsername, reach),
    reach,
    grantWireB64: reach === "grant" ? grantWireB64 : null,
    ...(reach === "open" && openBindingB64 !== null ? { openBindingB64 } : {}),
  };
}

/** Run one search. Never throws. */
export async function searchPeople(query: string, deps: PeopleSearchDeps): Promise<PeopleSearchOutcome> {
  const legs: LegOutcome[] = [];
  const username = deps.canonicalUsername(query);
  const term = deps.queryTerm(query);
  const probes: IntroductionProbe[] = [];
  /** What the people found by name state about their username, by key. */
  const nameClaims = new Map<string, UsernameClaim>();

  // ---- the channel namespace ------------------------------------------
  const channelLeg = (async (): Promise<{ idHex: string; handle: string } | null> => {
    if (username === null || deps.lookupChannel === undefined) return null;
    try {
      const idHex = await deps.lookupChannel(username);
      legs.push("answered");
      return idHex === null ? null : { idHex, handle: username };
    } catch (err) {
      legs.push(legOutcomeOf(err));
      return null;
    }
  })();

  // ---- the username ---------------------------------------------------
  // Written by the username question, read after all questions settled.
  const rounds: { username: OprfPair | null; term: OprfPair | null } = { username: null, term: null };
  const usernameLeg = (async (): Promise<PersonResult[]> => {
    if (username === null) return [];
    try {
      const round = await deps.usernameOprf(username);
      rounds.username = round;
      probes.push({ oprfOutput: round.output, via: "username" });
      const hit = await deps.accountOfUsernameLookupKey(round.lookupKey);
      if (hit === null) {
        legs.push("answered");
        return [];
      }
      const accountIdHex = toHex(hit.accountId);
      // The key comes from the identity bundle discovery serves for the
      // account, never from a profile's CLAIMED account id: any member of a
      // shared group could claim someone else's.
      const key = await identityKeyB64(deps, hit.accountId);
      legs.push("answered");
      if (key === null) return [];
      // The name the person published, when their record names this key.
      const published = acceptPublicRecord(deps, hit, round.output, key.bytes);
      // Discovery itself resolved this username to this account: that is
      // the binding a certificate would prove, so the username shows.
      const r = resultFor(
        deps,
        key.b64,
        key.hex,
        accountIdHex,
        "open",
        published ?? `@${username}`,
        username,
        null,
        publicRecordBinding(deps, hit, round.output, key.bytes),
      );
      return r === null ? [] : [r];
    } catch (err) {
      legs.push(legOutcomeOf(err));
      return [];
    }
  })();

  // ---- one name term --------------------------------------------------
  const nameLeg = (async (): Promise<PersonResult[]> => {
    if (term === null) return [];
    try {
      const round = await deps.nameTermOprf(term);
      rounds.term = round;
      probes.push({ oprfOutput: round.output, via: "term" });
      const hits = await deps.accountsOfNameTermLookupKey(round.lookupKey);
      const out: PersonResult[] = [];
      for (const hit of hits) {
        const accountIdHex = toHex(hit.accountId);
        const key = await identityKeyB64(deps, hit.accountId);
        if (key === null) continue;
        // A name this device holds, else the one the person published
        // (module docs); an unnamed hit is dropped.
        const published = acceptPublicRecord(deps, hit, round.output, key.bytes);
        const name = deps.known(key.b64).name ?? published;
        if (name === null || !deps.nameMatches(name, query)) continue;
        const r = resultFor(
          deps,
          key.b64,
          key.hex,
          accountIdHex,
          "open",
          published,
          null,
          null,
          publicRecordBinding(deps, hit, round.output, key.bytes),
        );
        if (r !== null) {
          out.push(r);
          const claim = publicRecordClaim(deps, hit, round.output, key.bytes);
          if (r.username === null && claim !== null) nameClaims.set(r.keyHex, claim);
        }
      }
      legs.push("answered");
      return out;
    } catch (err) {
      legs.push(legOutcomeOf(err));
      return [];
    }
  })();

  const [channel, byUsername, byNameOnly] = await Promise.all([channelLeg, usernameLeg, nameLeg]);

  // ---- public groups and channels (ADR-0087 section 5) ----------------
  // The same OPRF rounds: a search costs no token more than before. Matched
  // by `@name` exactly, or by the whole query against the name.
  const conversations: FoundConversation[] = [];
  if (deps.openConversationRecord !== undefined) {
    const seen = new Set<string>();
    const keep = (found: FoundConversation | null, byHandle: boolean): void => {
      if (found === null || seen.has(found.idB64)) return;
      if (byHandle ? found.handle !== username : !deps.nameMatches(found.name, query)) return;
      seen.add(found.idB64);
      conversations.push(found);
    };
    try {
      if (rounds.username !== null && deps.conversationRecordOfLookupKey !== undefined) {
        const rec = await deps.conversationRecordOfLookupKey(rounds.username.lookupKey);
        if (rec !== null) keep(deps.openConversationRecord(rec, rounds.username.output), true);
      }
      if (rounds.term !== null && deps.conversationRecordsOfTermLookupKey !== undefined) {
        for (const rec of await deps.conversationRecordsOfTermLookupKey(rounds.term.lookupKey)) {
          keep(deps.openConversationRecord(rec, rounds.term.output), false);
        }
      }
    } catch {
      // A failed conversation question costs only the conversations.
    }
  }

  // ---- usernames a record states ------------------------------------
  let extraChecks = 0;
  const lookupKeyOf =
    deps.usernameLookupKey ?? (async (u: string) => (await deps.usernameOprf(u)).lookupKey);
  /** `claim.username` once its certificate proves it, else `null`. */
  const provenUsername = async (claim: UsernameClaim): Promise<string | null> => {
    let lookupKey: Uint8Array | null = null;
    if (claim.username === username && rounds.username !== null) {
      lookupKey = rounds.username.lookupKey;
    } else if (extraChecks < MAX_EXTRA_USERNAME_CHECKS) {
      extraChecks += 1;
      try {
        lookupKey = await lookupKeyOf(claim.username);
      } catch {
        lookupKey = null;
      }
    }
    if (lookupKey === null) return null;
    return (await deps.verifyUsername(claim.certificate, lookupKey, claim.accountId)) ? claim.username : null;
  };
  const byName: PersonResult[] = [];
  for (const p of byNameOnly) {
    const claim = nameClaims.get(p.keyHex);
    const proven = claim === undefined ? null : await provenUsername(claim);
    byName.push(proven === null ? p : { ...p, username: shownUsername(proven, p.reach) });
  }

  // ---- contacts of contacts ------------------------------------------
  const byIntroduction: PersonResult[] = [];
  let introFailed = false;
  let recordsOpened = 0;
  let keptAfterMatch = 0;
  if (probes.length > 0) {
    let opened: OpenedIntroduction[] = [];
    try {
      opened = await deps.openIntroductions(probes);
    } catch {
      opened = [];
    }
    recordsOpened = opened.length;
    for (const intro of opened) {
      const matches =
        intro.via === "username"
          ? username !== null && intro.username === username
          : intro.displayName !== null && deps.nameMatches(intro.displayName, query);
      if (!matches) continue;
      keptAfterMatch += 1;
      // The record is the introduced person's own claim: its account id must
      // be the account the identity bundle publishes this key for, or a
      // record could pair its author's key with someone else's account id -
      // and that account's username certificate.
      let bound: boolean;
      try {
        const published = await deps.identityKeyOf(intro.accountId);
        bound = await boundToPublishedKey(deps, intro.identityPk, intro.accountId, published);
      } catch {
        introFailed = true;
        continue;
      }
      if (!bound) continue;
      const verified =
        intro.username !== null && intro.usernameCertificate !== null
          ? await provenUsername({
              username: intro.username,
              certificate: intro.usernameCertificate,
              accountId: intro.accountId,
            })
          : null;
      const keyB64 = keyB64Of(intro.identityPk);
      const name = intro.displayName ?? (verified !== null ? `@${verified}` : fingerprintLabel(intro.identityPk));
      const r = resultFor(
        deps,
        keyB64,
        toHex(intro.identityPk),
        toHex(intro.accountId),
        "grant",
        name,
        verified,
        deps.encodeGrant(intro.fofWire),
      );
      if (r !== null) byIntroduction.push(r);
    }
  }

  // ---- one list, then the reach check --------------------------------
  const merged = mergePeople([byUsername, byName, byIntroduction], deps.ownKeyHex);
  const people: PersonResult[] = [];
  let checkFailed = false;
  let refusedByReachCheck = 0;
  for (const p of merged) {
    if (p.reach !== "grant") {
      people.push(p);
      continue;
    }
    if (p.grantWireB64 === null) continue;
    try {
      const pk = base64Decode(p.keyB64);
      if (await deps.reachCheck(pk, p.grantWireB64)) people.push({ ...p, grantChecked: true });
      else refusedByReachCheck += 1;
    } catch {
      checkFailed = true;
    }
  }
  if (checkFailed || introFailed) legs.push("failed");
  deps.logCounts?.({
    recordsHeld: deps.introductionRecordsHeld?.() ?? 0,
    recordsOpened,
    keptAfterMatch,
    refusedByReachCheck,
    people: people.length,
  });
  const found = people.length + (channel === null ? 0 : 1) + conversations.length;
  return {
    people,
    channel,
    state: foldSearch(found, legs),
    ...(deps.openConversationRecord === undefined ? {} : { conversations }),
  };
}

/**
 * Whether a key found for an account is the account's: the key its identity
 * bundle publishes, or a PROVEN predecessor of it (ADR-0074 section 3.5a:
 * the same person, found through a record made before a key rotation). An
 * unproven mismatch is refused.
 */
export async function boundToPublishedKey(
  deps: Pick<PeopleSearchDeps, "provenPredecessor">,
  foundKey: Uint8Array,
  accountId: Uint8Array,
  published: Uint8Array | null,
): Promise<boolean> {
  if (published === null) return false;
  if (toHex(published) === toHex(foundKey)) return true;
  if (deps.provenPredecessor === undefined) return false;
  return deps.provenPredecessor(foundKey, accountId);
}

async function identityKeyB64(
  deps: PeopleSearchDeps,
  accountId: Uint8Array,
): Promise<{ b64: string; hex: string; bytes: Uint8Array } | null> {
  const key = await deps.identityKeyOf(accountId);
  if (key === null || key.length !== 32) return null;
  return { b64: keyB64Of(key), hex: toHex(key), bytes: key };
}
